import React, { useState } from 'react';
import { Popover, Select, Space } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useMutation } from 'react-query';
import { exportPositionList } from '@client/lib/api';
import Button from '@client/components/Button';
import showErrorNotification from '@client/utils/showErrorNotification';

export type PositionListExportType = 'activeContracts' | 'itineraries';

const EXPORT_OPTIONS = [
	{ label: 'Active contracts', value: 'activeContracts' as const },
	{ label: 'Position list (itineraries)', value: 'itineraries' as const },
];

const downloadBlob = (data: Blob, filename: string) => {
	const url = window.URL.createObjectURL(data);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};

const PositionListExport = () => {
	const [exportType, setExportType] = useState<PositionListExportType>('activeContracts');
	const [open, setOpen] = useState(false);

	const exportMutation = useMutation(
		(type: PositionListExportType) => exportPositionList(type),
		{
			onSuccess: (data, type) => {
				const suffix = type === 'activeContracts' ? 'active-contracts' : 'itineraries';
				downloadBlob(data, `position-list-${suffix}-${new Date().toISOString().slice(0, 10)}.xlsx`);
				setOpen(false);
			},
			onError: (error) => {
				showErrorNotification('Could not export position list', error as Error);
			},
		},
	);

	return (
		<Popover
			trigger="click"
			open={open}
			onOpenChange={setOpen}
			content={(
				<Space direction="vertical">
					<Select
						value={exportType}
						onChange={setExportType}
						options={EXPORT_OPTIONS}
						style={{ minWidth: 240 }}
					/>
					<Button
						type="primary"
						icon={<ExportOutlined />}
						disabled={exportMutation.isLoading}
						onClick={() => exportMutation.mutate(exportType)}
					>
						Export
					</Button>
				</Space>
			)}
		>
			<Button
				size="small"
				type="primary"
				icon={<ExportOutlined />}
				loading={exportMutation.isLoading}
			>
				Export to Excel
			</Button>
		</Popover>
	);
};

export default PositionListExport;
